import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { stringifyDetail } from '../../../lib/utils';

const API_BASE = window.location.origin;

// v3.5.0 — Survey Appearance: logo + background uploader.
// Reuses the existing /api/upload/logo endpoint (admin.py) — same validation,
// same types (PNG/JPEG/GIF/WebP/SVG), same 5MB limit, same storage pipeline.
const ImageUploadControl = ({
    value,                 // current image url or null
    onChange,              // (url|null) => void
    label,
    helpText,
    previewHeight = 80,    // CSS px — 100 for logo by default caller; 80 is a safe fallback
    previewMode = 'contain', // 'contain' for logos, 'cover' for background
    accept = 'image/png,image/jpeg,image/webp,image/svg+xml,image/gif',
    disabled = false,
    testIdPrefix = 'image-upload',
}) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        // Hard guard against non-image types (matches backend validation).
        if (!file.type.startsWith('image/')) {
            toast.error(t('surveys.appearance.onlyImages', 'Only image files are allowed.'));
            return;
        }
        const form = new FormData();
        form.append('file', file);
        setUploading(true);
        try {
            const token = localStorage.getItem('yulontap_token');
            const res = await fetch(`${API_BASE}/api/upload/logo`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                throw new Error(stringifyDetail(data.detail) || `Upload failed (${res.status})`);
            }
            onChange(data.url);
            toast.success(t('common.uploaded', 'Uploaded'));
        } catch (err) {
            toast.error(err.message || t('common.uploadFailed', 'Upload failed'));
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-control`}>
            {label && (
                <label className="text-sm font-medium">
                    {label}
                </label>
            )}
            {value ? (
                <div
                    className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-2 flex items-center gap-3"
                    data-testid={`${testIdPrefix}-preview-wrapper`}
                >
                    <div
                        className="flex-shrink-0 rounded bg-white dark:bg-slate-900 overflow-hidden border border-slate-100 dark:border-slate-700"
                        style={{ height: previewHeight, width: previewMode === 'cover' ? '160px' : 'auto' }}
                    >
                        <img
                            src={value}
                            alt={label || 'preview'}
                            className="block"
                            style={{
                                height: '100%',
                                width: previewMode === 'cover' ? '100%' : 'auto',
                                objectFit: previewMode,
                            }}
                            data-testid={`${testIdPrefix}-preview-img`}
                        />
                    </div>
                    <div className="flex flex-col gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={disabled || uploading}
                            onClick={() => inputRef.current?.click()}
                            data-testid={`${testIdPrefix}-replace-btn`}
                        >
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ImagePlus className="w-4 h-4 mr-1" />}
                            {t('surveys.appearance.replace', 'Replace')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600"
                            disabled={disabled || uploading}
                            onClick={() => onChange(null)}
                            data-testid={`${testIdPrefix}-remove-btn`}
                        >
                            <Trash2 className="w-4 h-4 mr-1" />
                            {t('surveys.appearance.remove', 'Remove')}
                        </Button>
                    </div>
                </div>
            ) : (
                <Button
                    type="button"
                    variant="outline"
                    disabled={disabled || uploading}
                    onClick={() => inputRef.current?.click()}
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                    {t('surveys.appearance.upload', 'Upload image')}
                </Button>
            )}
            {helpText && (
                <p className="text-xs text-muted-foreground">{helpText}</p>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid={`${testIdPrefix}-file-input`}
            />
        </div>
    );
};

export default ImageUploadControl;
